import React, { useContext, memo, useState } from 'react';
import { Hidden, AppBar, Toolbar, IconButton, Typography, MenuItem, Menu } from '@material-ui/core';
import { Menu as MenuIcon } from '@material-ui/icons';
import { AppLayoutContext } from '../AppLayout.context';
import { user } from 'services/auth.service';
import { useStyles } from './AppToolbar.styles';
import { isProdEnv, determineEnvText, determineToolBarColor } from 'services/apiEnv.service';
import LogoIcon from 'components/icons/Logo.icon';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronNewIcon, LogoutIcon } from 'components/icons';
import ProxyModal from 'components/common/Modal/ProxyModal';
import ProxyIcon from 'components/icons/ProxyIcon';
import { USERDATA_PROPERTY_NAME } from 'context/User.context';
import Tooltip from 'components/common/Tooltip/Tooltip';
import { AppCenterAPI } from 'api';
import { useToasts } from 'react-toast-notifications';
import { appCode, envCode } from '../../../../config/auth0.config';
import { ImpersonationParams } from 'api/app-center/app-center.types';

const DefaultMobileToolbar: React.FC = () => {
  const classes = useStyles();
  const { handleMobileDrawerToggle } = useContext(AppLayoutContext);

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={classes.toolbar}>
        <IconButton edge="start" onClick={handleMobileDrawerToggle} className={classes.iconButton}>
          <MenuIcon />
        </IconButton>
      </Toolbar>
    </AppBar>
  );
};

const DefaultDesktopToolbar: React.FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const { title, handleLogoClick } = useContext(AppLayoutContext);
  const { logout, user: auth0User } = useAuth0();
  const [openProxyModal, setOpenProxyModal] = useState(false);
  const { isImpersonated, permissions } = auth0User?.[USERDATA_PROPERTY_NAME];
  const { addToast } = useToasts();
  const impersonationListQuery = AppCenterAPI.useGetImpersonationList(
    { applicationCode: appCode, environmentCode: envCode, pageSize: 100 },
    1
  );
  const deleteImpersonation = AppCenterAPI.useDeleteImpersonation({
    onError: (error: any) => {
      const TIMEOUT = 5000;
      addToast('Error occurred while ending impersonation. Continuing with logout', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: TIMEOUT
      });
      setTimeout(
        () => logout({ logoutParams: { returnTo: window.location.origin, federated: true } }),
        TIMEOUT
      );
    }
  });

  async function handleLogout() {
    if (isImpersonated) {
      const { data } = await impersonationListQuery.refetch();
      const results = data?.data?.results ?? [];
      const userSessions = results.filter((session: ImpersonationParams) => {
        return session.originalUserSourceId === auth0User?.[USERDATA_PROPERTY_NAME].originalUserId;
      });
      userSessions.map((session: ImpersonationParams) => {
        deleteImpersonation.mutate(session.id ?? '');
      });
      logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
    } else {
      logout({ logoutParams: { returnTo: window.location.origin, federated: true } });
    }
  }

  return (
    <AppBar position="fixed" className={classes.appBar}>
      <Toolbar className={determineToolBarColor(classes)}>
        <IconButton edge="start" onClick={handleLogoClick} className={classes.iconButton}>
          <LogoIcon />
        </IconButton>

        <Typography
          color="inherit"
          noWrap
          className={!isProdEnv ? classes.playgroundTitle : classes.title}
        >
          {`Applied Request Center ${determineEnvText()}`}
        </Typography>

        <IconButton
          edge="end"
          color="inherit"
          onClick={(e: any) => {
            e.stopPropagation();
            setAnchorEl(e?.currentTarget);
          }}
          data-cy="username-apptoolbar"
          style={{ backgroundColor: 'transparent', textAlign: 'center' }}
          disableRipple
        >
          <Typography style={{ fontWeight: 600, fontSize: '14px' }}>
            {isImpersonated ? (
              <Tooltip title="End proxy session by logging out" placement="bottom" arrow>
                <span className="uppercase text-typography-dark bg-line-divider px-[6px] py-[3px] text-[12px] font-normal rounded-full mr-2">
                  Proxy
                </span>
              </Tooltip>
            ) : (
              ''
            )}{' '}
            {`${user.firstName} ${user.lastName}`}
          </Typography>
          <ChevronNewIcon
            fill="#fff"
            style={{ height: '10px', width: '10px', marginLeft: '8px' }} //marginLeft:'1px',marginTop:'3px'
          />
        </IconButton>
        <Menu
          id={'avatar-menu'}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          getContentAnchorEl={null}
        >
          <MenuItem style={{ width: '128px' }} onClick={handleLogout}>
            <LogoutIcon style={{ height: '14px', width: '14px', marginRight: '8px' }} />
            Log Out
          </MenuItem>
          {!isImpersonated && permissions.includes('Proxy.Access') && (
            <MenuItem onClick={() => setOpenProxyModal(true)}>
              <ProxyIcon className="h-4 w-4 text-icon mr-2" />
              Proxy
            </MenuItem>
          )}
        </Menu>
      </Toolbar>
      <ProxyModal open={openProxyModal} cancelUpdate={() => setOpenProxyModal(false)} />
    </AppBar>
  );
};

const RenderToolbar = () => (
  <>
    <Hidden smUp>
      <DefaultMobileToolbar />
    </Hidden>

    <Hidden xsDown>
      <DefaultDesktopToolbar />
    </Hidden>
  </>
);

const AppToolbar: React.FC = memo(RenderToolbar);

export default AppToolbar;
